import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactMessage } from '../../../types';

interface MessageCardProps {
  msg: ContactMessage;
  onDelete: (id: string) => void;
}

export function MessageCard({ msg, onDelete }: MessageCardProps) {
  const { t } = useTranslation();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedText, setCopiedText] = useState(false);

  const fullName = `${msg.firstName || ''} ${msg.lastName || ''}`.trim() || 'Anonymous';

  const getInitials = (): string => {
    const f = msg.firstName?.trim().charAt(0) || '';
    const l = msg.lastName?.trim().charAt(0) || '';
    return (f + l).toUpperCase() || '?';
  };

  const getAvatarColor = (name: string): string => {
    const colors = [
      '#e11d48', // Rose
      '#db2777', // Pink
      '#c084fc', // Purple
      '#818cf8', // Indigo
      '#3b82f6', // Blue
      '#06b6d4', // Cyan
      '#0d9488', // Teal
      '#10b981', // Emerald
      '#eab308', // Yellow
      '#f97316', // Orange
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isNew = msg.createdAt
    ? (Date.now() - new Date(msg.createdAt).getTime() < 24 * 60 * 60 * 1000)
    : false;

  const copyEmail = () => {
    void navigator.clipboard.writeText(msg.email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const copyMessage = () => {
    void navigator.clipboard.writeText(msg.message);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  const initials = getInitials();
  const avatarBg = getAvatarColor(fullName);

  return (
    <div className={`admin-message-card ${isNew ? 'new-message' : ''}`}>
      <div className="admin-message-card-inner">
        {/* Left: Avatar */}
        <div className="admin-message-avatar-container">
          <div
            className="admin-message-avatar"
            style={{ backgroundColor: avatarBg }}
          >
            {initials}
          </div>
          {isNew && <span className="admin-message-new-dot" />}
        </div>

        {/* Right: Content */}
        <div className="admin-message-content">
          <div className="admin-message-header">
            <div className="admin-message-sender-info">
              <div className="admin-message-name-wrapper">
                <span className="admin-message-name">{fullName}</span>
                {isNew && (
                  <span className="admin-message-new-badge">
                    {t('admin.newBadge', 'New')}
                  </span>
                )}
              </div>
              <div className="admin-message-email-container">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" className="email-icon">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <a href={`mailto:${msg.email}`} className="admin-message-email">
                  {msg.email}
                </a>
                <button
                  className={`admin-message-icon-btn copy-btn ${copiedEmail ? 'copied' : ''}`}
                  onClick={copyEmail}
                  title={t('admin.copyEmail', 'Copy Email')}
                >
                  {copiedEmail ? (
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="admin-message-top-right">
              <div className="admin-message-date-wrapper">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" className="clock-icon">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span className="admin-message-date">{formatDate(msg.createdAt)}</span>
              </div>
              <button
                className="admin-message-icon-btn delete-btn-circle"
                onClick={() => onDelete(msg.id)}
                title={t('admin.delete', 'Delete')}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </div>

          {/* Message Text Bubble */}
          <div className="admin-message-body-wrapper">
            <p className="admin-message-body">{msg.message}</p>
          </div>

          <div className="admin-message-footer-actions">
            <button
              className={`admin-message-action-btn copy-text-btn ${copiedText ? 'copied' : ''}`}
              onClick={copyMessage}
            >
              {copiedText ? (
                <>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{t('admin.copied', 'Copied')}</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  <span>{t('admin.copyMessage', 'Copy Message')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
